import { useEffect, useRef, useState } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Camera, Upload } from 'lucide-react';
import api from '../../utils/api';
import ModulePageHeading from '../../components/ModulePageHeading';
import { resolveNetworkNav } from '../../navigation/networkNavigation';

const emptyDraft = () => ({
  company_name: '',
  iqp_name: '',
  license_number: '',
  email: '',
  phone: '',
  ss_code: '',
});

function safeReturnPath(raw) {
  if (!raw || typeof raw !== 'string') return null;
  if (!raw.startsWith('/') || raw.startsWith('//')) return null;
  return raw;
}

function firstSsCode(org) {
  if (Array.isArray(org?.partner_ss_codes) && org.partner_ss_codes.length) {
    return String(org.partner_ss_codes[0]);
  }
  if (typeof org?.partner_ss_codes === 'string' && org.partner_ss_codes.trim()) {
    return org.partner_ss_codes.split(/[,;]+/)[0].trim();
  }
  const ssKeys = Object.keys(org?.iqps_by_ss || {});
  return ssKeys[0] || '';
}

/**
 * Same partner registration fields as Buildings ??IQP partners ??Register new partner.
 * ?fromOrg=id pre-fills from Agent company detail.
 */
export default function PartnerRegister() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const cameraRef = useRef(null);
  const fileRef = useRef(null);
  const fromOrgId = searchParams.get('fromOrg') || '';
  const returnTo =
    safeReturnPath(searchParams.get('return')) ||
    (fromOrgId ? `/network/org/${fromOrgId}` : '/network?tab=our-partner');

  const [draft, setDraft] = useState(emptyDraft);
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState('');
  const [extracting, setExtracting] = useState(false);
  const [prefilled, setPrefilled] = useState(false);

  const { data: fromOrgData } = useQuery({
    queryKey: ['service-network', 'org-buildings', fromOrgId],
    queryFn: async () =>
      (await api.get(`/service-network/organizations/${fromOrgId}/buildings`)).data?.data,
    enabled: !!fromOrgId,
  });

  const fromOrg = fromOrgData?.organization;

  useEffect(() => {
    if (!fromOrg || prefilled) return;
    setDraft({
      company_name: fromOrg.name || '',
      iqp_name: fromOrg.partner_iqp_name || fromOrg.iqp_name || fromOrg.contact_name || '',
      license_number: fromOrg.license_number || '',
      email: fromOrg.email || '',
      phone: fromOrg.phone || '',
      ss_code: firstSsCode(fromOrg),
    });
    setPrefilled(true);
  }, [fromOrg, prefilled]);

  const applyExtracted = (ex = {}) => {
    setDraft({
      company_name: ex.company_name || ex.name || '',
      iqp_name: ex.iqp_name || '',
      license_number: ex.license_number || '',
      email: ex.email || '',
      phone: ex.phone || '',
      ss_code: ex.ss_code || '',
    });
  };

  const setPhoto = async (file) => {
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    if (!file) {
      setPhotoFile(null);
      setPhotoPreview('');
      return;
    }
    setPhotoFile(file);
    setPhotoPreview(URL.createObjectURL(file));

    const isPdf = /\.pdf$/i.test(file.name) || file.type === 'application/pdf';
    const isTxt = /\.txt$/i.test(file.name) || file.type === 'text/plain';
    if (isPdf || isTxt) {
      setExtracting(true);
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api.post('/partner-companies/extract-preview', fd, { skipErrorToast: true });
        applyExtracted(res.data?.extracted || {});
        toast.success('Fields filled from document ??check and Save');
      } catch {
        /* user can fill manually */
      } finally {
        setExtracting(false);
      }
    }
  };

  const saveMutation = useMutation({
    mutationFn: async () => {
      const name = (draft.company_name || draft.iqp_name || '').trim();
      if (!name) throw new Error('Company or IQP name is required');

      let res;
      if (photoFile) {
        const fd = new FormData();
        fd.append('file', photoFile);
        fd.append('company_name', draft.company_name || name);
        fd.append('name', name);
        fd.append('iqp_name', draft.iqp_name || '');
        fd.append('license_number', draft.license_number || '');
        fd.append('email', draft.email || '');
        fd.append('phone', draft.phone || '');
        if (draft.ss_code) fd.append('ss_code', draft.ss_code);
        res = await api.post('/partner-companies/from-document', fd);
      } else {
        res = await api.post('/partner-companies', {
          name,
          contact_name: draft.iqp_name || null,
          iqp_name: draft.iqp_name || null,
          email: draft.email || null,
          phone: draft.phone || null,
          license_number: draft.license_number || null,
          ss_codes: draft.ss_code ? [draft.ss_code] : [],
        });
      }

      const partnerId = res.data?.data?.id;
      if (fromOrgId && partnerId) {
        try {
          await api.patch(`/service-network/organizations/${fromOrgId}`, {
            partner_company_id: partnerId,
          });
        } catch {
          /* non-blocking link */
        }
      }
      return res;
    },
    onSuccess: () => {
      toast.success(fromOrgId ? 'Partner registered from Agent company' : 'Partner registered');
      queryClient.invalidateQueries(['partner-companies']);
      queryClient.invalidateQueries(['service-network']);
      navigate(returnTo);
    },
    onError: (e) => toast.error(e.response?.data?.error || e.message || 'Failed to register'),
  });

  const backLabel = fromOrgId
    ? 'Back to company'
    : returnTo.includes('tab=agent')
      ? 'Back to Agent'
      : 'Back to Our Partner';

  return (
    <div>
      <ModulePageHeading
        resolveNav={resolveNetworkNav}
        title="IQP partners"
        showBack
        backTo={returnTo}
        backLabel={backLabel}
        infoTitle="IQP partners"
        infoChildren={
          <div className="space-y-2 text-sm">
            {fromOrgId ? (
              <p>
                Prefill from the Agent company. Check the fields, then Save to register under Our Partner.
              </p>
            ) : (
              <p>
                Register a partner company. Camera / Upload Form 12A fills fields when possible ??confirm, then Save.
              </p>
            )}
            <p>
              Directory:{' '}
              <Link to="/network?tab=our-partner" className="text-primary-700 underline">
                Our Partner
              </Link>
              .
            </p>
          </div>
        }
      />

      <div className="space-y-6 bg-card rounded-xl border border-gray-200 p-5 shadow-sm max-w-3xl">
        {fromOrgId && prefilled && (
          <p className="text-sm text-primary-800 bg-primary-50 border border-primary-100 rounded-lg px-3 py-2">
            Company details copied from Agent. Review and Save to register as Our Partner.
          </p>
        )}

        <div className="space-y-4 rounded-xl border border-dashed border-primary-300 bg-primary-50/40 p-4">
          <p className="text-xs text-gray-600">
            Optional: take a photo or upload PDF to refresh fields from Form 12A. Prefill from Agent already fills the
            form below.
          </p>

          <div className="flex flex-wrap gap-2">
            <input
              ref={cameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                setPhoto(e.target.files?.[0] || null);
                e.target.value = '';
              }}
            />
            <input
              ref={fileRef}
              type="file"
              accept="image/*,.pdf,.txt"
              className="hidden"
              onChange={(e) => {
                setPhoto(e.target.files?.[0] || null);
                e.target.value = '';
              }}
            />
            <button
              type="button"
              onClick={() => cameraRef.current?.click()}
              disabled={extracting}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-slate-800 text-white text-sm font-medium disabled:opacity-50"
            >
              <Camera className="w-4 h-4" />
              Camera (paper Form 12A)
            </button>
            <button
              type="button"
              onClick={() => fileRef.current?.click()}
              disabled={extracting}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-300 bg-white text-sm font-medium disabled:opacity-50"
            >
              <Upload className="w-4 h-4" />
              {extracting ? 'Reading…' : 'Upload photo / PDF'}
            </button>
            {photoFile && (
              <button
                type="button"
                onClick={() => setPhoto(null)}
                className="text-xs text-red-600 underline self-center"
              >
                Remove file
              </button>
            )}
          </div>

          {photoPreview && String(photoFile?.type || '').startsWith('image/') && (
            <div className="rounded-lg border bg-white overflow-hidden flex items-center justify-center max-h-56">
              <img src={photoPreview} alt="Form photo" className="max-h-56 max-w-full object-contain" />
            </div>
          )}
          {photoFile && !String(photoFile.type || '').startsWith('image/') && (
            <p className="text-xs text-gray-600">Attached: {photoFile.name}</p>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label className="block text-sm sm:col-span-2">
              <span className="font-medium text-gray-700">Company name *</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={draft.company_name}
                onChange={(e) => setDraft((d) => ({ ...d, company_name: e.target.value }))}
                placeholder="Partner company"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700">IQP name</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={draft.iqp_name}
                onChange={(e) => setDraft((d) => ({ ...d, iqp_name: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700">SS (hint)</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={draft.ss_code}
                onChange={(e) => setDraft((d) => ({ ...d, ss_code: e.target.value }))}
                placeholder="e.g. SS 15.2"
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700">Licence</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={draft.license_number}
                onChange={(e) => setDraft((d) => ({ ...d, license_number: e.target.value }))}
              />
            </label>
            <label className="block text-sm">
              <span className="font-medium text-gray-700">Email</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={draft.email}
                onChange={(e) => setDraft((d) => ({ ...d, email: e.target.value }))}
              />
            </label>
            <label className="block text-sm sm:col-span-2">
              <span className="font-medium text-gray-700">Phone</span>
              <input
                className="mt-1 w-full border rounded-lg px-3 py-2"
                value={draft.phone}
                onChange={(e) => setDraft((d) => ({ ...d, phone: e.target.value }))}
              />
            </label>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t pt-5">
          <button
            type="button"
            onClick={() => navigate(returnTo)}
            className="px-4 py-2.5 border rounded-lg text-sm"
          >
            Cancel
          </button>
          <button
            type="button"
            disabled={saveMutation.isPending || extracting}
            onClick={() => saveMutation.mutate()}
            className="px-5 py-2.5 bg-primary-600 text-white rounded-lg text-sm font-semibold hover:bg-primary-700 disabled:opacity-50"
          >
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
